import { doc, getDoc, onSnapshot, serverTimestamp, writeBatch } from 'firebase/firestore';

import { DomainError } from '@/core/errors/DomainError';
import {
  boardIdSchema,
  tournamentSchema,
  type Tournament,
  type TournamentId,
  type TournamentSettings,
  type Uid,
} from '@/core/schemas';
import { AppError, toAppError } from '@/infrastructure/errors/AppError';
import { firestore } from '@/infrastructure/firebase/app';
import { createConverter } from '@/infrastructure/firestore/converter';
import { firestorePaths } from '@/infrastructure/firestore/paths';

const converter = createConverter(tournamentSchema, 'tournaments');

/**
 * Live subscription to the tournament document, which carries the settings.
 *
 * Everything the engine and the forms need — number of boards, registration
 * state, maximum song length, match rules per round — is read from here, so a
 * change by the tournament director reaches every open device immediately.
 */
export function subscribeTournament(
  tournamentId: TournamentId,
  onData: (tournament: Tournament) => void,
  onError: (error: AppError) => void,
): () => void {
  const reference = doc(firestore, firestorePaths.tournament(tournamentId)).withConverter(
    converter,
  );

  return onSnapshot(
    reference,
    (snapshot) => {
      if (!snapshot.exists()) {
        onError(
          new AppError(
            'not-found',
            `Für dieses Turnier (${tournamentId}) wurden noch keine Daten angelegt.`,
          ),
        );
        return;
      }

      try {
        onData(snapshot.data());
      } catch (error) {
        onError(toAppError(error));
      }
    },
    (error) => {
      onError(toAppError(error));
    },
  );
}

/**
 * Creates the tournament document and its physical boards in one batch.
 *
 * Mirrors what {@link ../../../scripts/create-tournament.mjs} does — the CLI
 * variant stays for automated setups (e.g. seeding a staging project), the UI
 * variant is what the tournament director reaches for when a fresh deployment
 * comes up empty.
 *
 * Refuses if the document already exists so a mis-click never overwrites live
 * data.
 */
export async function createTournament(input: {
  tournamentId: TournamentId;
  settings: TournamentSettings;
  actorUid: Uid;
}): Promise<void> {
  const { tournamentId, settings, actorUid } = input;

  try {
    const reference = doc(firestore, firestorePaths.tournament(tournamentId));
    const existing = await getDoc(reference);
    if (existing.exists()) {
      throw new DomainError(
        'INVALID_STATE_TRANSITION',
        `Für die ID ${tournamentId} ist bereits ein Turnier angelegt.`,
      );
    }

    const batch = writeBatch(firestore);

    batch.set(reference, {
      id: tournamentId,
      settings,
      // `setup` — die Anmeldung wird bewusst über den eigenen Knopf auf der
      // Steuerungsseite geöffnet, damit Status und Einstellung gemeinsam
      // wandern (siehe setRegistrationOpen).
      status: 'setup',
      currentPhaseId: null,
      startedAt: null,
      completedAt: null,
      createdAt: serverTimestamp(),
      createdBy: actorUid,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });

    for (let number = 1; number <= settings.general.boardCount; number += 1) {
      const boardId = boardIdSchema.parse(`board-${String(number).padStart(2, '0')}`);
      batch.set(doc(firestore, firestorePaths.board(tournamentId, boardId)), {
        id: boardId,
        tournamentId,
        number,
        label: null,
        status: 'free',
        currentMatchId: null,
        createdAt: serverTimestamp(),
        createdBy: actorUid,
        updatedAt: serverTimestamp(),
        updatedBy: actorUid,
      });
    }

    await batch.commit();
  } catch (error) {
    throw toAppError(error);
  }
}
